import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import MigrationCrunchPage from './MigrationCrunchPage';
import { getPair } from '../data/migration_pairs';

// Thin SEO-only wrapper. Reads the :pair slug from the URL (e.g.
// "act-to-hubspot"), looks it up in MIGRATION_PAIRS, and renders the
// underlying MigrationCrunchPage with a pairConfig prop that overrides
// the hero copy, locks the source/destination, and sets <title> +
// <meta description> for the page.
//
// Unknown slugs render a dedicated "no guide for this combo yet" page
// with <meta name="robots" content="noindex"> so Google doesn't index
// garbage pair URLs as duplicate landing-page content.
export default function MigrationPairPage() {
  const { pair } = useParams();
  const config = getPair(pair);
  if (!config) {
    return <PairNotFound slug={pair} />;
  }
  return <MigrationCrunchPage pairConfig={config} />;
}

function PairNotFound({ slug }) {
  // Robots noindex + custom title so unknown pairs don't pollute SEO
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Migration guide not found — MigrationCrunch";
    let robots = document.querySelector('meta[name="robots"]');
    let created = false;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
      created = true;
    }
    const prevContent = robots.getAttribute('content');
    robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      document.title = prevTitle;
      if (created) {
        robots.remove();
      } else if (prevContent !== null) {
        robots.setAttribute('content', prevContent);
      }
    };
  }, []);

  // Try to humanize the slug ("foo-to-bar" -> "Foo → Bar")
  const humanized = (slug || '')
    .replace(/-to-/i, ' → ')
    .split(/[-→ ]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
    .replace(/\s*→\s*/g, ' → ');

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white flex items-center justify-center px-6 py-16" data-testid="pair-not-found">
      <div className="max-w-xl text-center">
        <div className="text-5xl mb-4">🧭</div>
        <h1 className="text-3xl font-black mb-3">
          We don't have a specific guide for{' '}
          <span className="text-amber-300">{humanized || 'that combo'}</span>{' '}
          yet
        </h1>
        <p className="text-white/70 mb-6">
          But our generic AI migration handles any CRM-to-CRM combination —
          even ones we haven't written a dedicated page for. Same patent-pending
          note re-association, same 5-tier pricing.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/migrate"
            className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors"
            data-testid="pair-not-found-start-btn"
          >
            Start a custom migration →
          </Link>
          <Link
            to="/migrate/act-to-generic"
            className="px-6 py-3 border border-white/30 hover:border-white/60 text-white font-medium rounded-md transition-colors"
            data-testid="pair-not-found-generic-btn"
          >
            See a sample guide
          </Link>
        </div>
        <p className="text-xs text-white/40 mt-8">
          Looking for a specific source/destination combo we should write next?{' '}
          <a href="mailto:chip@migrationcrunch.com" className="underline hover:text-white/70">
            Tell us
          </a>
          .
        </p>
      </div>
    </div>
  );
}
